import { InjectionToken } from '@angular/core';
import { interval, of } from 'rxjs';
import { catchError, filter, finalize, map, take, timeout } from 'rxjs/operators';
import get from 'lodash-es/get';
import isUndefined from 'lodash-es/isUndefined';

import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { DebugLogService } from '@finder/shared/services/debug-log/debug-log.service';
import { OneIdInstance } from '@finder/shared/services/one-id/one-id.interface';
import { OneIdService } from '@finder/shared/services/one-id/one-id.service';
import { ConfigService } from '@finder/core/config.service';
import { CONFIG_CONSTANTS } from '@finder/shared/constants/app.constants';
import { LocaleService } from '@finder/shared/services/locale-service/locale-service.service';
import { oneIdPlaceholder } from './one-id.constants';
import { LazyLoaderService } from '../lazy-loader/lazy-loader-service';

const logName = 'OneID Factory';

// The injected values are defined in app.module
export const OneIdFactory = async (
    windowRef: WindowRef,
    debugLog: DebugLogService,
    config: ConfigService,
    localeService: LocaleService,
    lazyLoader: LazyLoaderService,
) => new Promise((resolve, reject) => {
    // Create a dummy
    let oid: OneIdInstance = oneIdPlaceholder();
    let intervalIndex = 0;
    let loadDisneyId = false;

    const window = windowRef.nativeWindow;
    const disneyIdConfig = config.getValue(CONFIG_CONSTANTS.disneyId);
    const siteId = config.getValue(CONFIG_CONSTANTS.siteIdKey);

    const resolveOneIdService = () => {
        const oneIdService = new OneIdService(oid, debugLog);

        debugLog.log(`***** ${logName} finalize global scope`, oid, oneIdService);
        resolve(oneIdService);
    };

    const createDisneyId = () => {
        /**
         * We are creating a fallback if we can't find the global scope object
         * https://mywiki.disney.com/pages/viewpage.action?spaceKey=gamprofile&title=Profile+Authenticator
         *
         * If you want to test this locally you'll want to add this code to the top of interval subscribe
         * ```
         * loadDisneyId = true;
         * delete window.DisneyID;
         * return;
         * ```
         * This will delete the previously loaded DisneyID object and allow a new one to be
         * created
         */

        // First we load the authenticator-bundle so we can create a DisneyID object
        lazyLoader.lazyLoadScriptResource(disneyIdConfig.script, false)
            // Couldn't load the script. Return false so we stop
            .pipe(catchError(err => of(false)))
            .subscribe({
                next: (didScriptLoad) => {
                    // If we weren't able to load the auth bundle we should stop and use the dummy
                    if (!didScriptLoad) {
                        return resolveOneIdService();
                    }

                    // Once the authenticator-bundle script is loaded we can create a DisneyID object
                    // NOTE: You'll need a working language cookie for this to init
                    window.PEP.PROFILE_AUTH.init({
                        'oneIdScriptSrc': disneyIdConfig.oneIdScript,
                        'clientId': disneyIdConfig.clientId[siteId],
                        'cssOverride': disneyIdConfig.cssOverride[siteId],
                        'langPref': get(localeService.getLanguageCookie(), 'preferredLanguage'),
                        'loadScript': true,
                    })
                    .then(didInit => {
                        if (!didInit) {
                            // Script was not able to init. Resolve with dummy obj
                            return resolveOneIdService();
                        }

                        // Reference the DisneyID object on the global scope
                        oid = window.PEP.PROFILE_AUTH.did;
                        resolveOneIdService();
                    }).catch(err => {
                        // There was an issue trying to init DisneyID. Just use the dummy and move on
                        resolveOneIdService();
                    });
                }
            });
    };

    // We want to get the global singleton from `window` but we don't know when it will be
    // available so keep trying. If it takes 2 seconds just quit and return the dummy instance.
    // TODO: change this to throw rather than return the dummy and handle it in the UI component
    interval(250) // Check every quarter-second
        .pipe(
            map(() => {
                debugLog.log(`***** ${logName} checking`, ++intervalIndex, window.OneID || window.DisneyID);

                if (window.OneID) { // Check for v4 first
                    debugLog.log(`***** ${logName}: v4 found`, oid);
                } else if (window.DisneyID) { // Check for v2
                    debugLog.log(`***** ${logName}: v2 found`, oid);
                }

                return { oid: window.OneID || window.DisneyID };
            }),
            filter(val => !isUndefined(val.oid)),
            take(1),
            // Timeout triggers the error case in the subscriber
            timeout(2000), // Timeout after 2 seconds
            finalize(() => {
                // Worst case we created a dummy service and return that, but hopefully we have
                // the proper singleton and can create the service with it

                // Make sure DisneyId is supported on the site. HKDL is barfing
                if (!disneyIdConfig.clientId[siteId]) {
                    return resolveOneIdService();
                }

                // We need to manually load our own DisneyID instance
                if (loadDisneyId) {
                    createDisneyId();

                    return;
                }

                resolveOneIdService();
            }),
        )
        .subscribe(
            (result) => {
                // Test local creation of DisneyID
                // loadDisneyId = true; delete window.DisneyID; return;

                oid = result.oid.get() as OneIdInstance;
            },
            () => {
                loadDisneyId = true;
                debugLog.log(`***** ${logName} - manually load DisneyID`);
            });
});

export const ONE_ID_INSTANCE =
    new InjectionToken('ONE_ID_INSTANCE', {
        factory: (): Partial<OneIdInstance> => ({})
    });

